import { useEffect, useState } from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { toast } from 'sonner'
import { ConnectError, Code, createClient } from '@connectrpc/connect'
import { Link } from '@tanstack/react-router'

import { CreateContactRequestSchema, UpdateContactRequestSchema, GetContactByEmailRequestSchema, CortadoService } from '@/gen/cortado/v1/cortado_pb'
import type { Contact } from '@/gen/cortado/v1/contacts_pb'
import { useCreateContact, useUpdateContact } from '@/stores/contacts.store'
import { connectTransport } from '@/lib/connect-transport'
import { cn, formatPhoneInput, stripPhoneFormatting } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ActionGate } from '@/components/shared/action-gate'

interface ContactSheetProps {
  organizationId: string
  contact: Contact | null // null = create mode, Contact = edit mode
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Prefills the create form, e.g. from a sender the user wants to add. */
  initialValues?: ContactSheetInitialValues
  /** Called with the new contact after a successful create. */
  onCreated?: (contact: Contact) => void | Promise<void>
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
}

export interface ContactSheetInitialValues {
  firstName?: string
  lastName?: string
  email?: string
}

/**
 * ContactSheet - Sheet component for creating or editing contacts
 *
 * Features:
 * - Dual mode: create (contact = null) or edit (contact provided)
 * - Edit first name, last name, email, and phone number
 * - Uses TanStack Form for form state management
 * - Leverages useCreateContact/useUpdateContact hooks for optimized cache updates
 * - Displays loading states during mutation
 * - Shows toast notifications on success/error
 * - Handles "already exists" error with link to existing contact
 * - Resets form when sheet opens
 *
 * @param organizationId - The organization ID for creating contacts
 * @param contact - The contact to edit (null for create mode)
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function ContactSheet({
  organizationId,
  contact,
  open,
  onOpenChange,
  initialValues,
  onCreated,
  layout,
}: ContactSheetProps) {
  const { mutation: createMutation } = useCreateContact()
  const { mutation: updateMutation } = useUpdateContact()
  const [existingContact, setExistingContact] = useState<Contact | null>(null)

  const isEditMode = !!contact
  const isCreateMode = !isEditMode

  const form = useForm({
    defaultValues: {
      firstName: contact?.firstName ?? initialValues?.firstName ?? '',
      lastName: contact?.lastName ?? initialValues?.lastName ?? '',
      email: contact?.email ?? initialValues?.email ?? '',
      phoneNumber: formatPhoneInput(contact?.phoneNumber ?? ''),
    },
    onSubmit: async ({ value }) => {
      try {
        const firstName = value.firstName.trim()
        const lastName = value.lastName.trim()
        const email = value.email.trim()
        const phoneNumber = stripPhoneFormatting(value.phoneNumber)

        if (!firstName && !lastName && !email && !phoneNumber) {
          toast.error('Please enter at least one contact detail')
          return
        }

        if (isCreateMode) {
          const request = create(CreateContactRequestSchema, {
            organizationId,
            firstName,
            lastName,
            email: email || undefined,
            phoneNumber: phoneNumber || undefined,
          })

          const response = await createMutation.mutateAsync(request)
          toast.success('Contact created successfully')
          if (onCreated && response?.contact) {
            onOpenChange(false)
            form.reset()
            await onCreated(response.contact)
            return
          }
        } else {
          // Update existing contact
          const request = create(UpdateContactRequestSchema, {
            id: contact!.id,
            firstName,
            lastName,
            email: email || undefined,
            phoneNumber: phoneNumber || undefined,
            updateMask: create(FieldMaskSchema, {
              paths: ['first_name', 'last_name', 'email', 'phone_number'],
            }),
          })

          await updateMutation.mutateAsync(request)
          toast.success('Contact updated successfully')
        }

        onOpenChange(false)
        form.reset()
      } catch (error) {
        console.error('Failed to save contact:', error)
        reportError(error, { feature: 'contact_save', kind: 'mutation' })

        // Handle "already exists" error for create mode
        if (isCreateMode && error instanceof ConnectError && error.code === Code.AlreadyExists) {
          const email = value.email.trim()
          if (email) {
            try {
              // Fetch the existing contact by email
              const client = createClient(CortadoService, connectTransport)
              const response = await client.getContactByEmail(
                create(GetContactByEmailRequestSchema, { organizationId, email })
              )
              if (response.contact) {
                setExistingContact(response.contact)
                return // Don't show generic error toast
              }
            } catch {
              // Fall through to generic error
            }
          }
        }

        toast.error(isCreateMode ? 'Failed to create contact' : 'Failed to update contact', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  // Primitives, so an inline initialValues object does not reset the form on
  // every render.
  const initialFirstName = initialValues?.firstName ?? ''
  const initialLastName = initialValues?.lastName ?? ''
  const initialEmail = initialValues?.email ?? ''

  // Reset form when sheet opens with current contact data
  useEffect(() => {
    if (open) {
      setExistingContact(null) // Clear any previous existing contact error
      form.reset()
      form.setFieldValue('firstName', contact?.firstName ?? initialFirstName)
      form.setFieldValue('lastName', contact?.lastName ?? initialLastName)
      form.setFieldValue('email', contact?.email ?? initialEmail)
      form.setFieldValue('phoneNumber', formatPhoneInput(contact?.phoneNumber ?? ''))
    }
  }, [open, contact, form, initialFirstName, initialLastName, initialEmail])

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn(layout === 'pipeline' && 'w-full sm:max-w-xl')}>
        <SheetHeader>
          <SheetTitle>{isEditMode ? 'Edit Contact' : 'New Contact'}</SheetTitle>
          <SheetDescription>
            {isEditMode
              ? "Update the contact's information. Click save when you're done."
              : 'Create a new contact. Fill in the details below.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="contact-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
          <p className="text-sm text-muted-foreground">
            Enter at least one contact detail.
          </p>

          <form.Field name="firstName">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="first-name">First Name</Label>
                <Input
                  id="first-name"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isPending}
                  placeholder="Enter first name"
                />
              </div>
            )}
          </form.Field>

          <form.Field name="lastName">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="last-name">Last Name</Label>
                <Input
                  id="last-name"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isPending}
                  placeholder="Enter last name"
                />
              </div>
            )}
          </form.Field>

          <form.Field name="email">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isPending}
                  placeholder="Enter email address"
                />
              </div>
            )}
          </form.Field>

          <form.Field name="phoneNumber">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="phone-number">Phone Number</Label>
                <Input
                  id="phone-number"
                  type="tel"
                  inputMode="numeric"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(formatPhoneInput(e.target.value))}
                  onBlur={field.handleBlur}
                  disabled={isPending}
                  placeholder="(555) 123-4567"
                />
              </div>
            )}
          </form.Field>

          {/* Existing contact message */}
          {existingContact && (
            <div className="rounded-md bg-status-info-bg p-3 text-sm text-status-info-fg">
              Contact already exists.{' '}
              <Link
                to="/contacts/$id"
                params={{ id: existingContact.id }}
                className="font-medium underline"
                onClick={() => onOpenChange(false)}
              >
                View contact
              </Link>
            </div>
          )}

          </form>
        </SheetBody>
        <SheetFooter className={cn(layout === 'pipeline' && 'sm:justify-between')}>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </SheetClose>
          <form.Subscribe selector={(s) => [s.values, s.isSubmitting] as const}>
            {([values, isSubmitting]) => {
              const disabled =
                isPending ||
                isSubmitting ||
                (
                  !values.firstName.trim() &&
                  !values.lastName.trim() &&
                  !values.email.trim() &&
                  !stripPhoneFormatting(values.phoneNumber)
                )
              return (
                <ActionGate
                  organizationId={organizationId}
                  permission="ContactsManage"
                  verb={isEditMode ? 'edit' : 'create'}
                  noun="contacts"
                >
                  <Button type="submit" form="contact-form" disabled={disabled}>
                    {isPending ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Contact'}
                  </Button>
                </ActionGate>
              )
            }}
          </form.Subscribe>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

// Export the old name as an alias for backward compatibility during migration
export { ContactSheet as ContactEditSheet }
